// Module 11 — the delivered-cost SUPPLY CURVE (P13, demand D): the "cost of supplying from different
// plants" diagram. Pure `meritStack → SVG string` (no DOM, no uPlot), mirroring inspector.ts renderSldSvg;
// the mount injects it via the optional UiElement.innerHTML seam. Reads the cost-SORTED Snapshot.meritStack
// (every dispatched unit by P11 DELIVERED cost) and draws the classic merit-order stack: each unit is a bar
// whose WIDTH ∝ its dispatched MW (cumulative along x) and HEIGHT = its delivered $/MWh, coloured by fuel —
// so base/intermediate/peak read as a rising STACK and a remote plant sits visibly HIGHER than a near plant
// of equal bare cost. A dashed λ line crosses at the marginal (price-setting) unit, which is outlined.
import type { MeritStackEntry } from "../domain/index";

// Okabe-Ito CVD-safe palette, keyed by fuel family (ascending merit ≈ cool→warm).
const FUEL_COLOR: ReadonlyArray<readonly [string, string]> = [
  ["NUCLEAR", "#0072B2"], ["HYDRO", "#56B4E9"], ["WIND", "#009E73"], ["SOLAR", "#F0E442"],
  ["WASTE", "#009E73"], ["BIO", "#009E73"], ["BATTERY", "#CC79A7"],
  ["PEAKER", "#D55E00"], ["OIL", "#D55E00"], ["GAS", "#E69F00"],
];
function fuelColor(fuel: string): string {
  for (const [k, c] of FUEL_COLOR) if (fuel.includes(k)) return c;
  return "#999999";
}
const esc = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Returns "" for an empty stack (the panel then shows its placeholder text).
export function renderSupplyCurveSvg(meritStack: readonly MeritStackEntry[]): string {
  if (meritStack.length === 0) return "";
  const W = 320, H = 184, PL = 40, PB = 26, PT = 12, PR = 10;
  const plotW = W - PL - PR, plotH = H - PT - PB;
  const totalMw = meritStack.reduce((s, e) => s + e.mwDispatched, 0) || 1;
  const maxCost = Math.max(1, ...meritStack.map((e) => e.deliveredCostPerMwh)) * 1.12;
  const marginal = meritStack.find((e) => e.isMarginal);
  const lambda = marginal?.deliveredCostPerMwh ?? 0;
  const x = (mw: number): number => PL + (mw / totalMw) * plotW;
  const y = (c: number): number => PT + plotH - (c / maxCost) * plotH;
  const baseY = PT + plotH;

  let cum = 0;
  const bars: string[] = [];
  for (const e of meritStack) {
    const x0 = x(cum), x1 = x(cum + e.mwDispatched), yTop = y(e.deliveredCostPerMwh);
    const w = Math.max(0.6, x1 - x0);
    const outline = e.isMarginal ? ' stroke="#ffffff" stroke-width="1.5"' : ' stroke="#00000055" stroke-width="0.4"';
    bars.push(
      `<rect x="${x0.toFixed(1)}" y="${yTop.toFixed(1)}" width="${w.toFixed(1)}" height="${(baseY - yTop).toFixed(1)}" ` +
      `fill="${fuelColor(e.fuel)}"${outline}><title>${esc(e.name)} · ${esc(e.fuel)} · ${e.mwDispatched.toFixed(0)} MW @ ` +
      `$${e.deliveredCostPerMwh.toFixed(1)}/MWh${e.isImport ? " (import)" : ""}${e.isMarginal ? " — SETS λ" : ""}</title></rect>`,
    );
    cum += e.mwDispatched;
  }

  const yl = y(lambda);
  const lambdaLine = lambda > 0
    ? `<line x1="${PL}" y1="${yl.toFixed(1)}" x2="${W - PR}" y2="${yl.toFixed(1)}" stroke="#ffffff" stroke-width="1" stroke-dasharray="4 3"/>` +
      `<text x="${W - PR - 2}" y="${(yl - 3).toFixed(1)}" text-anchor="end" font-size="9" fill="#ffffff">λ $${lambda.toFixed(0)}${marginal ? " · " + esc(marginal.name.slice(0, 16)) : ""}</text>`
    : "";

  const axes =
    `<line x1="${PL}" y1="${PT}" x2="${PL}" y2="${baseY}" stroke="#888" stroke-width="0.7"/>` +
    `<line x1="${PL}" y1="${baseY}" x2="${W - PR}" y2="${baseY}" stroke="#888" stroke-width="0.7"/>` +
    `<text x="4" y="${(PT + 8).toFixed(1)}" font-size="9" fill="#aaa">$${maxCost.toFixed(0)}</text>` +
    `<text x="4" y="${baseY.toFixed(1)}" font-size="9" fill="#aaa">$/MWh</text>` +
    `<text x="${(PL + plotW / 2).toFixed(1)}" y="${H - 4}" text-anchor="middle" font-size="9" fill="#aaa">cumulative MW (→ ${totalMw.toFixed(0)})</text>`;

  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" class="supply-curve" role="img" ` +
    `aria-label="delivered-cost supply curve, marginal ${marginal ? esc(marginal.name) : "n/a"} at $${lambda.toFixed(0)}/MWh">` +
    `${axes}${bars.join("")}${lambdaLine}</svg>`;
}
